import { BRAND } from "./brand";
import { resolveDanceStyles, type DanceStyle } from "convex/danceStyleDefaults";

// The dance styles themselves are admin-managed in Convex; components read
// them through `useDanceStyles()`. These are the built-in defaults it falls
// back to — see convex/danceStyleDefaults.ts.
export {
  DEFAULT_DANCE_STYLES as DANCE_STYLES,
  DANCE_STYLE_GROUPS,
  type DanceStyle,
} from "convex/danceStyleDefaults";

/** The built-in styles by slug. Prefer `useDanceStyles().styleMap` in components. */
export const DANCE_STYLE_MAP = resolveDanceStyles(null).styleMap;

export const EVENT_TAGS = [
  { slug: "live-music", label: "Live Music" },
  { slug: "lesson-included", label: "Lesson Included" },
  { slug: "beginner-friendly", label: "Beginner Friendly" },
  { slug: "all-levels", label: "All Levels" },
  { slug: "social-only", label: "Social Only" },
  { slug: "workshop", label: "Workshop" },
  { slug: "late-night", label: "Late Night" },
  { slug: "outdoor", label: "Outdoor" },
  { slug: "themed-night", label: "Themed Night" },
  { slug: "competition", label: "Competition" },
  { slug: "performance", label: "Performance" },
  { slug: "free-admission", label: "Free Admission" },
  { slug: "pay-what-you-can", label: "Pay What You Can" },
  { slug: "lgbtq-friendly", label: "LGBTQ+ Friendly" },
] as const;

export const EVENT_TAG_MAP = Object.fromEntries(
  EVENT_TAGS.map((tag) => [tag.slug, tag])
) as Record<string, (typeof EVENT_TAGS)[number]>;

/** The theme's primary violet, for UI that takes a literal colour. */
export const BRAND_COLOR = BRAND.light.primary;

/**
 * The neutral used for an event whose style has no colour of its own — the
 * theme's muted grey-violet, so it reads as "unstyled" rather than as a style.
 */
export const DEFAULT_STYLE_COLOR = BRAND.light.mutedForeground;

/**
 * The colour that stands for an event, taken from the first style it lists.
 *
 * One event can carry several styles, but a dot or a block has room for one
 * colour; the first is the one the organizer led with. Pass the live
 * `styleMap` from `useDanceStyles()`; without one this reads the defaults.
 */
export function danceStyleColor(
  styles: readonly string[] | undefined,
  styleMap: Record<string, DanceStyle> = DANCE_STYLE_MAP
): string {
  return styleMap[styles?.[0] ?? ""]?.color ?? DEFAULT_STYLE_COLOR;
}
